import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { errorMessage, gebruiktMockApi, http } from './api'
import {
  mockGroep,
  mockGroepen,
  mockMaakGroep,
  mockMaakLesmoment,
  mockMaakLesreeks,
  mockVerwijderGroep,
  mockVerwijderLesmoment,
  mockVerwijderLesreeks,
  mockWijzigGroep,
  mockWijzigLesmoment,
  mockZetWeek,
} from './__fixtures__/beheer-fixtures'
import { vertraag } from './__fixtures__/vertraag'
import type {
  Groep,
  GroepKort,
  GroepWijziging,
  Lesmoment,
  LesmomentInvoer,
  Lesreeks,
  NieuweGroep,
  ZetbareWeekStatus,
} from './groep-types'
import { medewerkerKeys } from './medewerker-api'

export interface GroepApi {
  list(semesterId: string): Promise<GroepKort[]>
  get(semesterId: string, code: string): Promise<Groep>
  maak(semesterId: string, input: NieuweGroep): Promise<Groep>
  wijzig(semesterId: string, code: string, input: GroepWijziging): Promise<Groep>
  verwijder(code: string): Promise<void>
  maakLesreeks(semesterId: string, code: string, vak: string): Promise<Lesreeks>
  verwijderLesreeks(lesreeksId: string): Promise<void>
  zetWeek(
    semesterId: string,
    lesreeksId: string,
    maandag: string,
    status: ZetbareWeekStatus,
  ): Promise<Lesreeks>
  maakLesmoment(lesreeksId: string, input: LesmomentInvoer): Promise<Lesmoment>
  wijzigLesmoment(lesmomentId: string, input: Partial<LesmomentInvoer>): Promise<Lesmoment>
  verwijderLesmoment(lesmomentId: string): Promise<void>
}

function groepPad(code: string): string {
  return `/api/groepen/${encodeURIComponent(code)}`
}

class RealGroepApi implements GroepApi {
  list(semesterId: string): Promise<GroepKort[]> {
    return http.get<GroepKort[]>('/api/groepen', { semester: semesterId })
  }
  get(semesterId: string, code: string): Promise<Groep> {
    return http.get<Groep>(groepPad(code), { semester: semesterId })
  }
  maak(semesterId: string, input: NieuweGroep): Promise<Groep> {
    return http.post<Groep>('/api/groepen', input, { semester: semesterId })
  }
  wijzig(semesterId: string, code: string, input: GroepWijziging): Promise<Groep> {
    return http.patch<Groep>(groepPad(code), input, { semester: semesterId })
  }
  verwijder(code: string): Promise<void> {
    return http.del<void>(groepPad(code))
  }
  maakLesreeks(semesterId: string, code: string, vak: string): Promise<Lesreeks> {
    return http.post<Lesreeks>(`${groepPad(code)}/lesreeksen`, { vak }, { semester: semesterId })
  }
  verwijderLesreeks(lesreeksId: string): Promise<void> {
    return http.del<void>(`/api/lesreeksen/${lesreeksId}`)
  }
  zetWeek(
    semesterId: string,
    lesreeksId: string,
    maandag: string,
    status: ZetbareWeekStatus,
  ): Promise<Lesreeks> {
    return http.put<Lesreeks>(
      `/api/lesreeksen/${lesreeksId}/weken`,
      { maandag, status },
      { semester: semesterId },
    )
  }
  maakLesmoment(lesreeksId: string, input: LesmomentInvoer): Promise<Lesmoment> {
    return http.post<Lesmoment>(`/api/lesreeksen/${lesreeksId}/lesmomenten`, input)
  }
  wijzigLesmoment(lesmomentId: string, input: Partial<LesmomentInvoer>): Promise<Lesmoment> {
    return http.patch<Lesmoment>(`/api/lesmomenten/${lesmomentId}`, input)
  }
  verwijderLesmoment(lesmomentId: string): Promise<void> {
    return http.del<void>(`/api/lesmomenten/${lesmomentId}`)
  }
}

class MockGroepApi implements GroepApi {
  list(semesterId: string): Promise<GroepKort[]> {
    return vertraag(mockGroepen(semesterId))
  }
  get(semesterId: string, code: string): Promise<Groep> {
    return vertraag(mockGroep(semesterId, code))
  }
  maak(semesterId: string, input: NieuweGroep): Promise<Groep> {
    return vertraag(mockMaakGroep(semesterId, input), 160)
  }
  wijzig(semesterId: string, code: string, input: GroepWijziging): Promise<Groep> {
    return vertraag(mockWijzigGroep(semesterId, code, input), 120)
  }
  verwijder(code: string): Promise<void> {
    mockVerwijderGroep(code)
    return vertraag(undefined, 120)
  }
  maakLesreeks(semesterId: string, code: string, vak: string): Promise<Lesreeks> {
    return vertraag(mockMaakLesreeks(semesterId, code, vak), 160)
  }
  verwijderLesreeks(lesreeksId: string): Promise<void> {
    mockVerwijderLesreeks(lesreeksId)
    return vertraag(undefined, 120)
  }
  zetWeek(
    semesterId: string,
    lesreeksId: string,
    maandag: string,
    status: ZetbareWeekStatus,
  ): Promise<Lesreeks> {
    return vertraag(mockZetWeek(semesterId, lesreeksId, maandag, status), 100)
  }
  maakLesmoment(lesreeksId: string, input: LesmomentInvoer): Promise<Lesmoment> {
    return vertraag(mockMaakLesmoment(lesreeksId, input), 140)
  }
  wijzigLesmoment(lesmomentId: string, input: Partial<LesmomentInvoer>): Promise<Lesmoment> {
    return vertraag(mockWijzigLesmoment(lesmomentId, input), 120)
  }
  verwijderLesmoment(lesmomentId: string): Promise<void> {
    mockVerwijderLesmoment(lesmomentId)
    return vertraag(undefined, 120)
  }
}

export const groepApi: GroepApi = gebruiktMockApi ? new MockGroepApi() : new RealGroepApi()

export const groepKeys = {
  alle: ['groepen'] as const,
  lijst: (semesterId: string) => ['groepen', 'lijst', semesterId] as const,
  detail: (semesterId: string, code: string) => ['groepen', 'detail', semesterId, code] as const,
}

export function useGroepen(semesterId: string) {
  return useQuery({
    queryKey: groepKeys.lijst(semesterId),
    queryFn: () => groepApi.list(semesterId),
    enabled: semesterId !== '',
  })
}

export function useGroep(semesterId: string, code: string) {
  return useQuery({
    queryKey: groepKeys.detail(semesterId, code),
    queryFn: () => groepApi.get(semesterId, code),
    enabled: semesterId !== '' && code !== '',
  })
}

function useHerrekening(semesterId: string, code?: string) {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({ queryKey: groepKeys.lijst(semesterId) })
    queryClient.invalidateQueries({ queryKey: medewerkerKeys.team(semesterId) })
    if (code !== undefined) {
      queryClient.invalidateQueries({ queryKey: groepKeys.detail(semesterId, code) })
    }
  }
}

export function useMaakGroep(semesterId: string) {
  const herreken = useHerrekening(semesterId)
  return useMutation({
    mutationFn: (input: NieuweGroep) => groepApi.maak(semesterId, input),
    onSuccess: (groep) => {
      herreken()
      toast.success(`Groep ${groep.code} aangemaakt`)
    },
    onError: (error) => toast.error(errorMessage(error, 'Aanmaken mislukt')),
  })
}

export function useWijzigGroep(semesterId: string, code: string) {
  const queryClient = useQueryClient()
  const herreken = useHerrekening(semesterId, code)
  return useMutation({
    mutationFn: (input: GroepWijziging) => groepApi.wijzig(semesterId, code, input),
    onSuccess: (groep) => {
      queryClient.setQueryData(groepKeys.detail(semesterId, groep.code), groep)
      herreken()
      toast.success('Groep bijgewerkt')
    },
    onError: (error) => toast.error(errorMessage(error, 'Bijwerken mislukt')),
  })
}

export function useVerwijderGroep(semesterId: string) {
  const herreken = useHerrekening(semesterId)
  return useMutation({
    mutationFn: (code: string) => groepApi.verwijder(code),
    onSuccess: () => {
      herreken()
      toast.success('Groep verwijderd')
    },
    onError: (error) => toast.error(errorMessage(error, 'Verwijderen mislukt')),
  })
}

export function useMaakLesreeks(semesterId: string, code: string) {
  const herreken = useHerrekening(semesterId, code)
  return useMutation({
    mutationFn: (vak: string) => groepApi.maakLesreeks(semesterId, code, vak),
    onSuccess: (lesreeks) => {
      herreken()
      toast.success(`${lesreeks.vak} toegevoegd`)
    },
    onError: (error) => toast.error(errorMessage(error, 'Toevoegen mislukt')),
  })
}

export function useVerwijderLesreeks(semesterId: string, code: string) {
  const herreken = useHerrekening(semesterId, code)
  return useMutation({
    mutationFn: (lesreeksId: string) => groepApi.verwijderLesreeks(lesreeksId),
    onSuccess: () => {
      herreken()
      toast.success('Lesreeks verwijderd')
    },
    onError: (error) => toast.error(errorMessage(error, 'Verwijderen mislukt')),
  })
}

interface WeekMutatie {
  lesreeksId: string
  maandag: string
  status: ZetbareWeekStatus
}

export function useZetWeek(semesterId: string, code: string) {
  const queryClient = useQueryClient()
  const herreken = useHerrekening(semesterId, code)
  const sleutel = groepKeys.detail(semesterId, code)

  return useMutation({
    mutationFn: (vars: WeekMutatie) =>
      groepApi.zetWeek(semesterId, vars.lesreeksId, vars.maandag, vars.status),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: sleutel })
      const vorige = queryClient.getQueryData<Groep>(sleutel)

      queryClient.setQueryData<Groep>(sleutel, (groep) =>
        groep
          ? {
              ...groep,
              lesreeksen: groep.lesreeksen.map((reeks) =>
                reeks.id === vars.lesreeksId
                  ? {
                      ...reeks,
                      weken: reeks.weken.map((week) =>
                        week.maandag === vars.maandag ? { ...week, status: vars.status } : week,
                      ),
                    }
                  : reeks,
              ),
            }
          : groep,
      )

      return { vorige }
    },
    onError: (error, _vars, context) => {
      if (context?.vorige) queryClient.setQueryData(sleutel, context.vorige)
      toast.error(errorMessage(error, 'Week bijwerken mislukt'))
    },
    onSuccess: (lesreeks) => {
      queryClient.setQueryData<Groep>(sleutel, (groep) =>
        groep
          ? {
              ...groep,
              lesreeksen: groep.lesreeksen.map((reeks) =>
                reeks.id === lesreeks.id ? lesreeks : reeks,
              ),
            }
          : groep,
      )
    },
    onSettled: () => herreken(),
  })
}

export function useMaakLesmoment(semesterId: string, code: string) {
  const herreken = useHerrekening(semesterId, code)
  return useMutation({
    mutationFn: (vars: { lesreeksId: string; input: LesmomentInvoer }) =>
      groepApi.maakLesmoment(vars.lesreeksId, vars.input),
    onSuccess: () => {
      herreken()
      toast.success('Lesmoment toegevoegd')
    },
    onError: (error) => toast.error(errorMessage(error, 'Toevoegen mislukt')),
  })
}

export function useWijzigLesmoment(semesterId: string, code: string) {
  const herreken = useHerrekening(semesterId, code)
  return useMutation({
    mutationFn: (vars: { lesmomentId: string; input: Partial<LesmomentInvoer> }) =>
      groepApi.wijzigLesmoment(vars.lesmomentId, vars.input),
    onSuccess: () => {
      herreken()
      toast.success('Lesmoment bijgewerkt')
    },
    onError: (error) => toast.error(errorMessage(error, 'Bijwerken mislukt')),
  })
}

export function useVerwijderLesmoment(semesterId: string, code: string) {
  const herreken = useHerrekening(semesterId, code)
  return useMutation({
    mutationFn: (lesmomentId: string) => groepApi.verwijderLesmoment(lesmomentId),
    onSuccess: () => {
      herreken()
      toast.success('Lesmoment verwijderd')
    },
    onError: (error) => toast.error(errorMessage(error, 'Verwijderen mislukt')),
  })
}
